import { useCallback, useState, type FormEvent } from 'react'
import { apiRequest, HttpError, publicWebRoutes, TurnstileField } from '@shared'
import type { PublicActionResponse } from '@features/Services/shared/types'

export function ContactForm() {
  const [turnstileToken, setTurnstileToken] = useState('')
  const [turnstileKey, setTurnstileKey] = useState(0)
  const [error, setError] = useState('')
  const [sent, setSent] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const handleTurnstileToken = useCallback((token: string) => setTurnstileToken(token), [])

  function refreshTurnstile() {
    setTurnstileToken('')
    setTurnstileKey((value) => value + 1)
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError('')
    setSent(false)

    const form = event.currentTarget
    const data = new FormData(form)
    const name = String(data.get('name') ?? '').trim()
    const email = String(data.get('email') ?? '').trim()
    const subject = String(data.get('subject') ?? '').trim()
    const message = String(data.get('message') ?? '').trim()
    if (!name || !email || !subject || !message) {
      setError('Please fill in all required fields.')
      return
    }

    setSubmitting(true)
    try {
      const result = await apiRequest<PublicActionResponse>(publicWebRoutes.contactMessage, {
        method: 'POST',
        data: {
          name,
          email,
          subject,
          message,
          turnstileToken,
        },
      })
      if (!result.success) {
        throw new HttpError(result.message)
      }
      setSent(true)
      form.reset()
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Could not send your message. Please try again.')
    } finally {
      refreshTurnstile()
      setSubmitting(false)
    }
  }

  return (
    <form className="email-form" onSubmit={onSubmit} autoComplete="off">
      <div className="row">
        <div className="col-12 col-md-6 form-group">
          <input
            type="text"
            name="name"
            id="name"
            className="form-control"
            placeholder="Your Name"
            maxLength={100}
            required
          />
        </div>
        <div className="col-12 col-md-6 form-group">
          <input
            type="email"
            name="email"
            id="email"
            className="form-control"
            placeholder="Your Email"
            maxLength={80}
            required
          />
        </div>
      </div>
      <div className="form-group">
        <input
          type="text"
          name="subject"
          id="subject"
          className="form-control"
          placeholder="Subject"
          maxLength={150}
          required
        />
      </div>
      <div className="form-group">
        <textarea
          className="form-control"
          name="message"
          id="message"
          rows={5}
          placeholder="Message"
          maxLength={2000}
          required
        />
      </div>
      <TurnstileField
        key={turnstileKey}
        onToken={handleTurnstileToken}
      />
      <div className="my-3">
        <div className={submitting ? 'loading d-block' : 'loading'}>Loading</div>
        <div className={error ? 'error-message d-block' : 'error-message'}>{error}</div>
        <div className={sent ? 'sent-message d-block' : 'sent-message'}>
          Your message has been sent. Thank you!
        </div>
      </div>
      <div className="text-center">
        <button type="submit" disabled={submitting}>
          Send Message
        </button>
      </div>
    </form>
  )
}
